import { motion } from 'framer-motion'
import { useMemo } from 'react'

import type { Bracket, BracketMatch, BracketTeam } from '@/lib/api/schemas'

/**
 * Tournament Stadium — single-elimination bracket renderer.
 *
 * Column-per-round layout with right-angle connector lines drawn as
 * absolutely-positioned divs (cheap, off-main-thread, scales with
 * `transform` cleanly when we shrink to 30% during the final-match
 * transition in S.8).
 *
 * Each match card carries a top team row + a bottom team row. Winner
 * highlights green; loser dims. The focus match (whatever the
 * MatchFocusCard is currently zooming in on) gets a primary-blue
 * border so the viewer can see the matching position on the bracket.
 *
 * Layout math: round N's vertical spacing = 2^N × round-1 spacing.
 * That's the standard single-elimination bracket geometry (each
 * subsequent round halves the match count, so the survivors fan out
 * to twice the spacing). Pure CSS via the `--row-h` and `--gap-h`
 * vars on each column.
 */
export function StadiumBracket({
  bracket,
  focusMatchId,
  recentlyCompleted,
}: {
  bracket: Bracket
  focusMatchId: string | null
  /** Match-ids that flipped to COMPLETE in the last ~3s. Drives the
   *  per-card flash + winner highlight. Optional — defaults to empty
   *  so callers without diff data still get the static bracket. */
  recentlyCompleted?: ReadonlySet<string>
}) {
  // Group matches by round, sorted by matchIndex within each.
  const byRound = useMemo(() => groupByRound(bracket.matches ?? []), [bracket])

  if (byRound.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p
          className="font-display font-medium uppercase"
          style={{
            color: 'var(--tv-fg-subtle)',
            letterSpacing: '0.3em',
            fontSize: 'clamp(0.85rem, 1vw, 1.05rem)',
          }}
        >
          Bracket loading…
        </p>
      </div>
    )
  }

  return (
    <div
      className="flex h-full items-stretch gap-[clamp(20px,1.6vw,32px)] overflow-hidden px-[clamp(12px,1.2vw,24px)] py-[clamp(12px,1.2vh,20px)]"
      role="img"
      aria-label="Tournament bracket"
    >
      {byRound.map((round, idx) => (
        <RoundColumn
          key={idx}
          round={round}
          roundIndex={idx}
          totalRounds={byRound.length}
          focusMatchId={focusMatchId}
          recentlyCompleted={recentlyCompleted}
        />
      ))}
    </div>
  )
}

function RoundColumn({
  round,
  roundIndex,
  totalRounds,
  focusMatchId,
  recentlyCompleted,
}: {
  round: BracketMatch[]
  roundIndex: number
  totalRounds: number
  focusMatchId: string | null
  recentlyCompleted?: ReadonlySet<string>
}) {
  const isFinal = roundIndex === totalRounds - 1
  return (
    // `flex-1` (NOT `shrink basis-0` — that collapses to zero width when
    // nothing inside forces the column wider, leaving the match cards
    // as thin vertical lines). `min-w-0` keeps the contents allowed to
    // shrink below their intrinsic content size, so two columns split
    // the available width evenly even on a 4K viewport.
    <div className="flex min-w-0 flex-1 flex-col">
      <RoundHeader index={roundIndex} total={totalRounds} />
      <div
        // `justify-evenly` distributes match cards along the column
        // height — combined with the column-per-round shrink, the
        // standard bracket pyramid emerges naturally. No need for
        // explicit spacing multipliers per round.
        className="flex min-h-0 flex-1 flex-col justify-evenly gap-[clamp(4px,0.5vh,12px)]"
      >
        {round.map((m) => (
          <MatchCard
            key={m.matchId}
            match={m}
            isFocus={m.matchId === focusMatchId}
            isFinal={isFinal}
            justCompleted={recentlyCompleted?.has(m.matchId) ?? false}
          />
        ))}
      </div>
    </div>
  )
}

function RoundHeader({ index, total }: { index: number; total: number }) {
  const label = roundColumnLabel(index, total)
  return (
    <p
      // `whitespace-nowrap` is critical — the wide letter-spacing
      // combined with narrow columns was wrapping "SEMIFINAL" mid-word
      // ("S E MFIR" / "N A L"). truncate keeps long round labels inside
      // their column on small brackets without breaking the layout.
      className="font-display mb-[clamp(8px,1vh,16px)] truncate text-center font-extrabold whitespace-nowrap uppercase"
      style={{
        color: 'var(--tv-fg-subtle)',
        letterSpacing: '0.22em',
        fontSize: 'clamp(0.65rem, 0.75vw, 0.85rem)',
      }}
    >
      {label}
    </p>
  )
}

function roundColumnLabel(index: number, total: number): string {
  const fromEnd = total - 1 - index
  if (fromEnd === 0) return 'Final'
  if (fromEnd === 1) return 'Semifinal'
  if (fromEnd === 2) return 'Quarterfinal'
  if (fromEnd === 3) return 'Round of 16'
  if (fromEnd === 4) return 'Round of 32'
  return `Round ${index + 1}`
}

function MatchCard({
  match,
  isFocus,
  isFinal,
  justCompleted,
}: {
  match: BracketMatch
  isFocus: boolean
  isFinal: boolean
  justCompleted: boolean
}) {
  const status = (match.status ?? 'PENDING').toUpperCase()
  const winnerId = match.winnerTeamId ?? null
  const a = match.participantA ?? null
  const b = match.participantB ?? null

  // Border accent: just-completed beats final beats focus beats default.
  // The just-completed accent fires for ~3s after the diff detector
  // catches the transition, then naturally fades back to the next-
  // priority colour.
  const accent = justCompleted
    ? 'var(--tv-live)'
    : isFinal
      ? 'var(--tv-gold)'
      : isFocus
        ? 'var(--tv-primary)'
        : 'var(--tv-border)'
  const live = status === 'IN_PROGRESS' || status === 'AWAITING_CONFIRMATION'

  // Just-completed treatment: a single 1.0 → 1.04 → 1.0 scale pulse +
  // brief green glow + flash background. Per the Emil framework —
  // Occasional-class moment (10-15× per tournament), so restrained:
  // 320ms total, no spring, full transform strings for HW accel, and
  // we use `motion.div` instead of CSS keyframes so React can interrupt
  // the animation cleanly if the match flips state again mid-flash.
  //
  // The bezier ease is typed as a fixed tuple — Framer Motion only
  // accepts that shape OR a named string. `as const` keeps inference
  // narrow enough that the prop type-checks.
  const EASE_OUT_STRONG = [0.23, 1, 0.32, 1] as const
  const cardAnimate = justCompleted
    ? { transform: ['scale(1)', 'scale(1.04)', 'scale(1)'] }
    : { transform: 'scale(1)' }
  const cardTransition = justCompleted
    ? {
        duration: 0.4,
        ease: EASE_OUT_STRONG,
        times: [0, 0.4, 1],
      }
    : { duration: 0 }

  return (
    <motion.div
      initial={{ transform: 'scale(1)' }}
      animate={cardAnimate}
      transition={cardTransition}
      className="overflow-hidden rounded-xl border bg-[var(--tv-bg-elev)]"
      style={{
        borderColor: accent,
        // Minimum-height anchor so a single-match round (e.g. 2-entrant
        // bracket = one match) doesn't collapse to a sliver. clamp()
        // scales the min from a readable 1080p baseline through 4K+.
        minHeight: 'clamp(80px, 9vh, 160px)',
        // Slight luminance on the focus + final + just-completed cards.
        // The just-completed glow is the strongest because it's
        // transient — the eye needs to find it quickly.
        boxShadow: justCompleted
          ? '0 0 40px -8px rgba(0, 255, 136, 0.55)'
          : isFocus
            ? '0 0 24px -8px rgba(61, 111, 255, 0.55)'
            : isFinal
              ? '0 0 24px -8px rgba(255, 179, 0, 0.4)'
              : 'none',
        transition: 'border-color 280ms ease, box-shadow 280ms ease',
      }}
    >
      {live && !winnerId && <LiveStrip />}
      <TeamRow
        team={a}
        winner={!!winnerId && winnerId === a?.teamId}
        loser={!!winnerId && winnerId !== a?.teamId}
        live={live && !winnerId}
      />
      <div
        style={{
          height: 1,
          backgroundColor: 'var(--tv-divider)',
        }}
      />
      <TeamRow
        team={b}
        winner={!!winnerId && winnerId === b?.teamId}
        loser={!!winnerId && winnerId !== b?.teamId}
        live={live && !winnerId}
      />
    </motion.div>
  )
}

/**
 * Pulsing "Live" ribbon for a match currently being played. Sits above
 * the two team rows so it never overlaps team names (the alternative —
 * an absolutely-positioned corner badge — clashed with long truncated
 * names on narrow columns). Reuses the same `stadium-live-dot` pulse +
 * `--tv-live` accent as the header indicator and the focus card's
 * "On now" badge, so a viewer scanning the whole bracket recognises
 * the same live language everywhere on screen.
 */
function LiveStrip() {
  return (
    <div
      className="font-display flex items-center gap-[clamp(4px,0.4vw,8px)] px-[clamp(10px,0.9vw,16px)] font-extrabold uppercase"
      style={{
        backgroundColor: 'rgba(0, 255, 136, 0.12)',
        color: 'var(--tv-live)',
        letterSpacing: '0.2em',
        fontSize: 'clamp(0.55rem, 0.6vw, 0.7rem)',
        paddingTop: 'clamp(3px,0.35vh,6px)',
        paddingBottom: 'clamp(3px,0.35vh,6px)',
      }}
    >
      <span
        className="stadium-live-dot inline-block shrink-0 rounded-full"
        aria-hidden
        style={{
          width: 'clamp(5px, 0.4vw, 7px)',
          height: 'clamp(5px, 0.4vw, 7px)',
          backgroundColor: 'var(--tv-live)',
        }}
      />
      Live
    </div>
  )
}

function TeamRow({
  team,
  winner,
  loser,
  live,
}: {
  team: BracketTeam | null
  winner: boolean
  loser: boolean
  live: boolean
}) {
  const name = teamDisplayName(team)
  const initials = teamInitials(team)
  const rowAccent = winner
    ? 'var(--tv-live)'
    : loser
      ? 'var(--tv-fg-subtle)'
      : live
        ? 'var(--tv-primary)'
        : 'var(--tv-fg)'
  const avatarUrl = teamAvatar(team)
  return (
    <div
      className="flex items-center gap-[clamp(8px,0.7vw,12px)] px-[clamp(10px,0.9vw,16px)] py-[clamp(8px,0.9vh,14px)]"
      style={{
        opacity: loser ? 0.5 : 1,
        backgroundColor: winner ? 'rgba(0, 255, 136, 0.06)' : 'transparent',
        transition: 'opacity 200ms ease, background-color 240ms ease',
      }}
    >
      <div
        // Avatar slot. Render the team's image when one's available (so
        // your team logo shows in the bracket cards too — not just on the
        // focus pane); fall back to the monogram tile when the team is
        // TBD or has no image. Border matches the row accent so winners
        // get a green ring + losers stay muted.
        className="flex shrink-0 items-center justify-center overflow-hidden rounded-md font-display font-black"
        style={{
          width: 'clamp(32px, 2.6vw, 96px)',
          height: 'clamp(32px, 2.6vw, 96px)',
          backgroundColor: 'var(--tv-bg-surface)',
          border: avatarUrl ? `2px solid ${winner ? 'var(--tv-live)' : 'var(--tv-border)'}` : 'none',
          color: rowAccent,
          fontSize: 'clamp(0.7rem, 0.85vw, 0.95rem)',
          letterSpacing: '-0.02em',
          transition: 'border-color 240ms ease',
        }}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          initials
        )}
      </div>
      <span
        className="font-display flex-1 truncate font-semibold"
        style={{
          color: rowAccent,
          fontSize: 'clamp(0.9rem, 1.05vw, 1.15rem)',
        }}
      >
        {name}
      </span>
      {winner && (
        <span
          aria-hidden
          className="font-display shrink-0 font-extrabold"
          style={{
            color: 'var(--tv-live)',
            fontSize: 'clamp(0.85rem, 1vw, 1.1rem)',
            letterSpacing: '-0.02em',
          }}
        >
          ✓
        </span>
      )}
    </div>
  )
}

// ─── helpers ────────────────────────────────────────────────────────

function groupByRound(matches: BracketMatch[]): BracketMatch[][] {
  // Filter to the main-bracket matches — drop bronze + lower-bracket
  // for the column layout; they'd break the geometry. The MatchFocusCard
  // can still surface them as the focus match (they get the Bronze
  // Match / Lower Bracket label), so they're not invisible to viewers.
  const winners = matches.filter((m) => m.kind === 'W' || m.kind === 'GF')
  const byRound = new Map<number, BracketMatch[]>()
  for (const m of winners) {
    const r = typeof m.round === 'number' ? m.round : 0
    const list = byRound.get(r) ?? []
    list.push(m)
    byRound.set(r, list)
  }
  return [...byRound.keys()]
    .sort((a, b) => a - b)
    .map((r) =>
      (byRound.get(r) ?? []).sort((a, b) => (a.matchIndex ?? 0) - (b.matchIndex ?? 0)),
    )
}

function teamDisplayName(team: BracketTeam | null): string {
  // TBD reads more meaningfully than "—" or empty space — it tells
  // viewers the slot is genuinely awaiting a winner from a previous
  // round (vs a data bug).
  if (!team) return 'Awaiting winner'
  if (team.teamLabel?.trim()) return team.teamLabel
  const members = team.members ?? []
  if (members.length === 1) return members[0].displayName ?? members[0].callsign ?? 'Player'
  if (members.length > 0) return members[0].displayName ?? 'Team'
  return 'Team'
}

function teamInitials(team: BracketTeam | null): string {
  if (!team) return '?'
  const name = teamDisplayName(team)
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

/**
 * Pick a representative avatar for the team. Mirrors MatchFocusCard's
 * picker:
 *   - solo (1 member) → that member's avatarUrl
 *   - team (2+ members) → the first member's avatarUrl as the team mark
 *     (the host can swap by re-ordering members; future work could
 *     surface a team-logo upload on the squadron-team admin screen).
 *   - no avatar / null team → null; caller falls back to initials.
 */
function teamAvatar(team: BracketTeam | null): string | null {
  if (!team) return null
  const members = team.members ?? []
  if (members.length > 0 && members[0].avatarUrl) return members[0].avatarUrl
  return null
}
